import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Checkbox,
  Group,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconArrowDown,
  IconArrowUp,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { usePermission } from "@how/app-kit";
import type { Enums } from "@how/database";
import {
  ConfirmActionModal,
  ErrorState,
  LoadingState,
  PageHeader,
  RecordDrawer,
  useSearchParamState,
} from "@how/ui";
import {
  formFields,
  useDeleteForm,
  useDeleteSubmission,
  useForm,
  useSetSubmissionStatus,
  useUpdateForm,
  type FormDetail,
  type FormField,
  type FormFields,
  type SubmissionRow,
  submissionMatch,
} from "./form-queries";
import { fieldKey } from "./slug";
import { SubmissionMatch } from "./SubmissionMatch";
import { SubmissionPerson } from "./SubmissionPerson";
import { semanticColor } from "@how/design-system";

const TYPES: { readonly value: Enums<"form_field_type">; readonly label: string }[] = [
  { value: "text", label: "Short answer" },
  { value: "long_text", label: "Long answer" },
  { value: "email", label: "Email address" },
  { value: "phone", label: "Phone number" },
  { value: "choice", label: "Choose one" },
  { value: "checkbox", label: "Tick box" },
  { value: "consent", label: "Consent to be contacted" },
];

const CHANNELS = [
  { value: "email", label: "Email" },
  { value: "sms", label: "Text message" },
  { value: "phone", label: "Phone" },
  { value: "mail", label: "Post" },
];

export function FormEditorPage() {
  const { formId = null } = useParams();
  const form = useForm(formId);
  if (form.isPending) return <LoadingState />;
  if (form.isError) {
    return (
      <ErrorState
        message="Could not load this form. It may have been deleted."
        onRetry={() => {
          void form.refetch();
        }}
      />
    );
  }
  return <FormEditor key={form.data.form.id} detail={form.data} />;
}

function FormEditor({ detail }: { readonly detail: FormDetail }) {
  const navigate = useNavigate();
  const { form, submissions } = detail;
  const canManage = usePermission("forms.manage", { write: true });
  const manage = canManage.data === true;
  const [tab, setTab] = useSearchParamState("tab", "submissions");
  const [open, setOpen] = useState<SubmissionRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const update = useUpdateForm();
  const remove = useDeleteForm();
  const unread = submissions.filter((s) => s.status === "new").length;

  const [fields, setFields] = useState<FormFields>({
    name: form.name,
    description: form.description ?? "",
    thankYouMessage: form.thank_you_message ?? "",
    isActive: form.is_active,
    forAdults: form.for_adults,
    fields: formFields(form.fields),
  });
  const set =
    (key: "name" | "description" | "thankYouMessage") =>
    (event: { currentTarget: { value: string } }) => {
      const next = event.currentTarget.value;
      setFields((current) => ({ ...current, [key]: next }));
    };
  const setField = (index: number, patch: Partial<FormField>) => {
    setFields((current) => ({
      ...current,
      fields: current.fields.map((field, i) => (i === index ? { ...field, ...patch } : field)),
    }));
  };
  const moveField = (from: number, to: number) => {
    setFields((current) => {
      const next = [...current.fields];
      const [moved] = next.splice(from, 1);
      if (moved !== undefined) next.splice(to, 0, moved);
      return { ...current, fields: next };
    });
  };

  return (
    <>
      <PageHeader
        title={form.name}
        description={form.description ?? "A form visitors can send from a page on your website."}
        above={
          <Group gap="xs">
            <Anchor component={Link} to="/website/forms" size="sm">
              Forms
            </Anchor>
            {form.is_active ? (
              <Badge variant="light" color="teal">
                Taking messages
              </Badge>
            ) : (
              <Badge variant="light" color="gray">
                Closed
              </Badge>
            )}
            {unread > 0 ? (
              <Badge variant="light" color={semanticColor("info")}>
                {unread} unread
              </Badge>
            ) : null}
          </Group>
        }
        actions={
          manage ? (
            <Button
              variant="default"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={() => {
                setConfirmDelete(true);
              }}
            >
              Delete form
            </Button>
          ) : undefined
        }
      />

      <Tabs
        value={tab}
        onChange={(next) => {
          setTab(next ?? undefined);
        }}
      >
        <Tabs.List mb="md">
          <Tabs.Tab value="submissions">What people sent ({submissions.length})</Tabs.Tab>
          <Tabs.Tab value="questions">Questions</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="submissions">
          {submissions.length === 0 ? (
            <Text size="sm" c="dimmed">
              Nothing yet. Put this form on a page with a contact or newsletter block, publish the
              page, and what visitors send arrives here.
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={520}>
              <Table verticalSpacing="sm" highlightOnHover aria-label="Submissions">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Received</Table.Th>
                    <Table.Th>From</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {submissions.map((submission) => {
                    const answers = submission.data as Record<string, unknown>;
                    const first = Object.values(answers).find(
                      (v) => typeof v === "string" && v.trim() !== "",
                    );
                    return (
                      <Table.Tr key={submission.id}>
                        <Table.Td>
                          <Text size="sm">
                            {new Date(submission.submitted_at).toLocaleString()}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" truncate>
                            {typeof first === "string" ? first : "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <SubmissionStatus submission={submission} />
                        </Table.Td>
                        <Table.Td ta="right">
                          <Button
                            variant="subtle"
                            size="xs"
                            aria-label={`Open the message received on ${new Date(submission.submitted_at).toLocaleString()}`}
                            onClick={() => {
                              setOpen(submission);
                            }}
                          >
                            Open
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="questions">
          <Stack gap="sm" maw={720}>
            <TextInput
              label="Name"
              required
              disabled={!manage}
              value={fields.name}
              onChange={set("name")}
            />
            <Textarea
              label="Description"
              description="Shown to staff, not to visitors."
              disabled={!manage}
              autosize
              minRows={2}
              value={fields.description}
              onChange={set("description")}
            />
            <Textarea
              label="What a visitor sees after sending"
              disabled={!manage}
              autosize
              minRows={2}
              value={fields.thankYouMessage}
              onChange={set("thankYouMessage")}
            />
            <Switch
              label="Taking messages"
              description="Switched off, the form disappears from the page and sends nothing."
              disabled={!manage}
              checked={fields.isActive}
              onChange={(event) => {
                const next = event.currentTarget.checked;
                setFields((current) => ({ ...current, isActive: next }));
              }}
            />
            <Switch
              label="This form is for adults (18 and over)"
              description={
                fields.fields.some((f) => f.type === "consent")
                  ? "A sign-up is matched to your people when it arrives. A yes to texts is recorded then only on a form for adults; otherwise you confirm it, because children are never texted."
                  : "Matters once the form has a Consent to be contacted question: a yes to texts is then recorded when a sign-up arrives only on a form for adults."
              }
              disabled={!manage}
              checked={fields.forAdults}
              onChange={(event) => {
                const next = event.currentTarget.checked;
                setFields((current) => ({ ...current, forAdults: next }));
              }}
            />

            {fields.fields.map((field, index) => (
              <Paper key={`${field.key}-${String(index)}`} withBorder p="md" radius="md">
                <Stack gap="xs">
                  <Group justify="space-between" wrap="nowrap">
                    <Text size="sm" fw={600}>
                      Question {index + 1}
                    </Text>
                    {manage ? (
                      <Group gap={4} wrap="nowrap">
                        <ActionIcon
                          variant="subtle"
                          aria-label={`Move question ${String(index + 1)} up`}
                          disabled={index === 0}
                          onClick={() => {
                            moveField(index, index - 1);
                          }}
                        >
                          <IconArrowUp size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          aria-label={`Move question ${String(index + 1)} down`}
                          disabled={index === fields.fields.length - 1}
                          onClick={() => {
                            moveField(index, index + 1);
                          }}
                        >
                          <IconArrowDown size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label={`Remove question ${String(index + 1)}`}
                          onClick={() => {
                            setFields((current) => ({
                              ...current,
                              fields: current.fields.filter((_, i) => i !== index),
                            }));
                          }}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    ) : null}
                  </Group>
                  <TextInput
                    label="Question"
                    required
                    disabled={!manage}
                    value={field.label}
                    onChange={(event) => {
                      const label = event.currentTarget.value;
                      // The key is how an answer is stored, so it follows the question until the form is saved.
                      setField(index, { label, key: fieldKey(label) || field.key });
                    }}
                  />
                  <Group grow align="flex-start">
                    <Select
                      label="Answer"
                      disabled={!manage}
                      allowDeselect={false}
                      data={TYPES}
                      value={field.type}
                      onChange={(next) => {
                        if (next === null) return;
                        const type = next as Enums<"form_field_type">;
                        // What a type needs is set when the type is chosen, not left for the save to refuse: a
                        // consent field must name its channel, and a choice field needs somewhere to put options.
                        setField(index, {
                          type,
                          ...(type === "consent"
                            ? { channel: field.channel ?? "email" }
                            : { channel: undefined }),
                          ...(type === "choice"
                            ? { options: field.options ?? [] }
                            : { options: undefined }),
                        });
                      }}
                    />
                    {field.type === "consent" ? (
                      <Select
                        label="Consent to receive"
                        description="Recorded as evidence with the message."
                        disabled={!manage}
                        allowDeselect={false}
                        data={CHANNELS}
                        value={field.channel ?? "email"}
                        onChange={(next) => {
                          if (next !== null) setField(index, { channel: next });
                        }}
                      />
                    ) : field.type === "choice" ? (
                      <TextInput
                        label="Choices"
                        description="Separated by commas."
                        disabled={!manage}
                        value={(field.options ?? []).join(", ")}
                        onChange={(event) => {
                          const next = event.currentTarget.value;
                          setField(index, {
                            options: next
                              .split(",")
                              .map((option) => option.trim())
                              .filter((option) => option.length > 0),
                          });
                        }}
                      />
                    ) : (
                      <div />
                    )}
                  </Group>
                  <Checkbox
                    label="A visitor must answer this"
                    disabled={!manage}
                    checked={field.required === true}
                    onChange={(event) => {
                      const next = event.currentTarget.checked;
                      setField(index, { required: next });
                    }}
                  />
                </Stack>
              </Paper>
            ))}

            {manage ? (
              <Group justify="space-between">
                <Button
                  variant="light"
                  leftSection={<IconPlus size={16} />}
                  disabled={fields.fields.length >= 30}
                  onClick={() => {
                    setFields((current) => ({
                      ...current,
                      fields: [
                        ...current.fields,
                        {
                          key: `question_${String(current.fields.length + 1)}`,
                          label: "",
                          type: "text",
                          required: false,
                        },
                      ],
                    }));
                  }}
                >
                  Add question
                </Button>
                <Button
                  loading={update.isPending}
                  disabled={
                    fields.name.trim().length === 0 ||
                    fields.fields.some((f) => f.label.trim() === "")
                  }
                  onClick={() => {
                    update.mutate(
                      { formId: form.id, fields, savedForAdults: form.for_adults },
                      {
                        onSuccess: () => {
                          notifications.show({
                            color: "teal",
                            title: "Saved",
                            message: "The form is updated wherever it appears.",
                          });
                        },
                        onError: (error) => {
                          notifications.show({
                            color: "red",
                            title: "Not saved",
                            message: error.message,
                          });
                        },
                      },
                    );
                  }}
                >
                  Save form
                </Button>
              </Group>
            ) : null}
            {update.isError ? (
              <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
                {update.error.message}
              </Alert>
            ) : null}
          </Stack>
        </Tabs.Panel>
      </Tabs>

      {open === null ? null : (
        <SubmissionDrawer
          key={open.id}
          // The row as last read, so the drawer sees a person added from it.
          submission={submissions.find((s) => s.id === open.id) ?? open}
          formId={form.id}
          fields={formFields(form.fields)}
          canManage={manage}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}

      <ConfirmActionModal
        opened={confirmDelete}
        onClose={() => {
          setConfirmDelete(false);
        }}
        title="Delete this form?"
        message={`${form.name} will stop appearing on any page that uses it. What people have already sent is kept.`}
        confirmLabel="Delete form"
        danger
        loading={remove.isPending}
        onConfirm={() => {
          remove.mutate(form.id, {
            onSuccess: () => {
              notifications.show({ color: "teal", title: "Form deleted", message: form.name });
              void navigate("/website/forms");
            },
          });
        }}
      />
    </>
  );
}

function SubmissionDrawer({
  submission,
  formId,
  fields,
  canManage,
  onClose,
}: {
  readonly submission: SubmissionRow;
  readonly formId: string;
  readonly fields: readonly FormField[];
  readonly canManage: boolean;
  readonly onClose: () => void;
}) {
  const setStatus = useSetSubmissionStatus();
  const remove = useDeleteSubmission();
  const [confirming, setConfirming] = useState(false);
  const answers = submission.data as Record<string, unknown>;
  const label = (key: string) => fields.find((field) => field.key === key)?.label ?? key;
  const match = submissionMatch(submission);
  const shown = (value: unknown) =>
    value === true ? "Yes" : value === false ? "No" : String(value);

  // Opening a message is reading it; the church should not have to say so twice. After render, not during it, and
  // once per message: the mutation object changes as it runs (pending, then done) while the list still says "new"
  // until it refetches, so depending on it sent the request again on every change, hundreds of times a second.
  const markRead = setStatus.mutate;
  const markedRead = useRef<string | null>(null);
  useEffect(() => {
    if (submission.status !== "new" || !canManage || markedRead.current === submission.id) return;
    markedRead.current = submission.id;
    markRead({ formId, submissionId: submission.id, status: "read" });
  }, [submission.id, submission.status, canManage, formId, markRead]);

  return (
    <>
      <RecordDrawer
        opened
        onClose={onClose}
        title="Message"
        subtitle={
          <Text size="sm" c="dimmed" mb="md">
            Received {new Date(submission.submitted_at).toLocaleString()}
          </Text>
        }
      >
        <Stack gap="md">
          {Object.entries(answers).map(([key, value]) => (
            <Stack key={key} gap={2}>
              <Text size="sm" fw={600}>
                {label(key)}
              </Text>
              <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                {shown(value)}
              </Text>
            </Stack>
          ))}
          {submission.consent_channels.length > 0 ? (
            <Alert color="teal" variant="light" title="Consent given">
              This person agreed to be contacted by {submission.consent_channels.join(", ")}.{" "}
              {submission.person_id === null && match.outcome === "awaiting_confirmation"
                ? "It is recorded when they confirm by email."
                : submission.person_id === null
                  ? "It becomes a communication preference when you add them to your people."
                  : match.outcome === "matched" || match.outcome === "created"
                    ? "What was recorded is below."
                    : "It was recorded as their communication preference when they were added."}
            </Alert>
          ) : null}
          <SubmissionPerson
            formId={formId}
            submissionId={submission.id}
            personId={submission.person_id}
            matchOutcome={match.outcome}
          />
          <SubmissionMatch
            formId={formId}
            submissionId={submission.id}
            personId={submission.person_id}
            outcome={match.outcome}
            details={match.details}
          />
          {canManage ? (
            <Group justify="space-between">
              <Button
                variant="subtle"
                color="red"
                onClick={() => {
                  setConfirming(true);
                }}
              >
                Delete
              </Button>
              <Button
                variant="default"
                loading={setStatus.isPending}
                onClick={() => {
                  setStatus.mutate(
                    {
                      formId,
                      submissionId: submission.id,
                      status: submission.status === "archived" ? "read" : "archived",
                    },
                    { onSuccess: onClose },
                  );
                }}
              >
                {submission.status === "archived" ? "Put back" : "Archive"}
              </Button>
            </Group>
          ) : null}
        </Stack>
      </RecordDrawer>
      <ConfirmActionModal
        opened={confirming}
        onClose={() => {
          setConfirming(false);
        }}
        title="Delete this message?"
        message="It is removed for everyone, and cannot be brought back."
        confirmLabel="Delete"
        danger
        loading={remove.isPending}
        onConfirm={() => {
          remove.mutate({ formId, submissionId: submission.id }, { onSuccess: onClose });
        }}
      />
    </>
  );
}

/**
 * A sign-up's status in the list: a decision waiting for staff first (#338 part 2), then whether it became a person
 * and how, then whether anyone has read it. Always in words, never by colour alone.
 */
function SubmissionStatus({ submission }: { readonly submission: SubmissionRow }) {
  const { outcome } = submissionMatch(submission);
  const [label, role] =
    submission.person_id === null && outcome === "needs_decision"
      ? (["Needs you", "warning"] as const)
      : submission.person_id === null && outcome === "awaiting_confirmation"
        ? (["Waiting to confirm", "info"] as const)
        : submission.person_id !== null && outcome === "matched"
          ? (["Matched", "success"] as const)
          : submission.person_id !== null && outcome === "created"
            ? (["New person", "success"] as const)
            : submission.person_id !== null
              ? (["Added", "neutral"] as const)
              : submission.status === "new"
                ? (["New", "info"] as const)
                : submission.status === "read"
                  ? (["Read", "neutral"] as const)
                  : (["Archived", "neutral"] as const);
  return (
    <Badge variant="light" color={semanticColor(role)}>
      {label}
    </Badge>
  );
}
